import {
  Component,
  computed,
  inject,
  OnInit,
  OnDestroy,
  DestroyRef,
  effect,
} from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  CalistActions,
  calistFeature,
  calistGenerateCrlFeature,
  calistRenewFeature,
  calistRevokeFeature,
  selectCalistDrilldownKey,
  selectCalistEditItemKey,
  selectSaving,
} from '../calist.feature';
import { CaDetailsComponent } from '../components/ca-details/ca-details.component';
import { MainTableComponent } from '../components/main-table/main-table.component';
import { UpdateCaRequestDto } from 'src/app/shared/services/api/dto/update-ca-request-dto';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { CreateCaComponent } from '../components/create-ca/create-ca.component';
import { CA } from 'src/app/shared/entities/ca';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { CaRevokeReason } from 'src/app/shared/entities/ca-revoke-reason';

@Component({
  selector: 'app-calist',
  imports: [
    CaDetailsComponent,
    MainTableComponent,
    CreateCaComponent,
    SpinnerComponent,
  ],
  templateUrl: './calist.component.html',
  styleUrl: './calist.component.scss',
})
export class CalistComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private calistDataService = inject(CalistDataService);
  private apiService = inject(ApiService);
  private langService = inject(LangService);
  private toastrService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  private labels = this.langService.lang.features.calist;

  private pageParams$ = this.store.select(calistFeature.selectGridData);
  pageParams = toSignal(this.pageParams$);
  private drilldownCaName = toSignal(
    this.store.select(selectCalistDrilldownKey),
  );
  private editCaName = toSignal(this.store.select(selectCalistEditItemKey));
  drilldown = computed(() => {
    const drilldownCaName = this.drilldownCaName();
    return drilldownCaName
      ? this.pageParams()?.rowData.find(
          ({ caName }) => caName === this.drilldownCaName(),
        )
      : undefined;
  });
  edit = computed(() => {
    const editCaName = this.editCaName();
    return editCaName
      ? this.pageParams()?.rowData.find(({ caName }) => caName === editCaName)
      : undefined;
  });
  saving = toSignal(this.store.select(selectSaving));
  renewing = toSignal(this.store.select(calistRenewFeature.selectRenewing));
  revoking = toSignal(this.store.select(calistRevokeFeature.selectRevoking));
  spinning = computed(() => {
    const revoking = this.revoking();
    const renewing = this.renewing();
    return !!revoking || !!renewing;
  });
  crl = toSignal(this.store.select(calistGenerateCrlFeature.selectCrl));

  onBack() {
    this.store.dispatch(CalistActions.backFromDrilldown());
  }

  onSave(event: { ca: CA; dto: UpdateCaRequestDto }) {
    this.store.dispatch(
      CalistActions.save({ originalObject: event.ca, edit: event.dto }),
    );
  }

  onCancel() {
    this.store.dispatch(CalistActions.cancel({ confirmation: false }));
  }

  csr?: string;
  loadingCsr = false;
  importingCertChain = false;
  importedCertChain = false;

  onNeedCsr({ caName }: { caName: string }) {
    this.loadingCsr = true;
    this.apiService
      .requestCsr(caName)
      .pipe(
        tap(csr => {
          this.loadingCsr = false;
          this.csr = csr;
        }),
        catchError(error => {
          this.loadingCsr = false;
          this.toastrService.error(
            extractErrorMessage(error) || this.labels.toasts.defaultCsrError,
          );
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private clearCsrEffect = () => {
    const edit = this.edit();
    if (edit) {
      this.csr = undefined;
    }
  };

  onImportCertChain({
    caName,
    certChain,
  }: {
    caName: string;
    certChain: string;
  }) {
    this.importingCertChain = true;
    this.apiService
      .importCertChain(caName, certChain)
      .pipe(
        tap(() => {
          this.importingCertChain = false;
          this.importedCertChain = true;
          this.toastrService.success(this.labels.toasts.importSuccess);
          this.calistDataService.transform(cas =>
            cas.map(ca =>
              ca.caName === caName
                ? { ...ca, certificateChain: certChain.split('\n') }
                : ca,
            ),
          );
        }),
        catchError(error => {
          this.importingCertChain = false;
          this.importedCertChain = false;
          this.toastrService.error(
            extractErrorMessage(error) || this.labels.toasts.defaultImportError,
          );
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onRenew(ca: CA) {
    this.store.dispatch(CalistActions.renew({ ca }));
  }

  onRevoke(event: {
    ca: CA;
    reason: CaRevokeReason;
    revokeLeafCertificates: boolean;
  }) {
    this.store.dispatch(CalistActions.revoke(event));
  }

  onGenerateCrl(ca: CA) {
    this.store.dispatch(CalistActions.generateCrl({ ca }));
  }

  onCloseCrl() {
    this.store.dispatch(CalistActions.generateCrlReset());
  }

  ngOnInit() {
    this.calistDataService.ensure(); // ensure the data is reloaded
  }

  ngOnDestroy() {
    this.calistDataService.clear();
  }

  constructor() {
    effect(this.clearCsrEffect);
  }
}
